import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin } from 'lucide-react';
import { addressService, AddressSuggestion } from '../../services/addressService';

const DEBOUNCE_MS = 300;

interface AddressAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onSelect: (suggestion: AddressSuggestion) => void;
    inputClassName: string;
    isRTL: boolean;
}

type SearchStatus = 'idle' | 'ok' | 'empty' | 'error';

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
    value,
    onChange,
    onSelect,
    inputClassName,
    isRTL,
}) => {
    const { t } = useTranslation();
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [status, setStatus] = useState<SearchStatus>('idle');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    // Après un choix, le parent met à jour la valeur : ne pas relancer de recherche pour ce changement
    const skipNextSearch = useRef(false);

    useEffect(() => {
        if (skipNextSearch.current) {
            skipNextSearch.current = false;
            return;
        }

        const query = value.trim();
        if (query.length < addressService.minQueryLength) {
            setSuggestions([]);
            setStatus('idle');
            setOpen(false);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const results = await addressService.search(query, controller.signal);
                setSuggestions(results);
                setStatus(results.length > 0 ? 'ok' : 'empty');
                setActiveIndex(-1);
                // N'ouvrir la liste que si l'utilisateur est en train de saisir
                setOpen(document.activeElement === inputRef.current);
            } catch (error: any) {
                if (error?.name === 'AbortError') return;
                setSuggestions([]);
                setStatus('error');
                setOpen(document.activeElement === inputRef.current);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [value]);

    const select = (suggestion: AddressSuggestion) => {
        if (suggestion.street !== value) {
            skipNextSearch.current = true;
        }
        setOpen(false);
        setActiveIndex(-1);
        onSelect(suggestion);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => (suggestions.length === 0 ? -1 : (i + 1) % suggestions.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => (suggestions.length === 0 ? -1 : (i <= 0 ? suggestions.length - 1 : i - 1)));
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            // Ne pas soumettre le formulaire quand on valide une suggestion
            e.preventDefault();
            select(suggestions[activeIndex]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const listId = 'address-autocomplete-list';
    const showList = open && status !== 'idle';

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                required
                autoComplete="off"
                role="combobox"
                aria-expanded={showList}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                className={inputClassName}
                value={value}
                placeholder={t('register.addressInfo.searchPlaceholder')}
                onChange={e => onChange(e.target.value)}
                onFocus={() => { if (status !== 'idle') setOpen(true); }}
                onBlur={() => setOpen(false)}
                onKeyDown={handleKeyDown}
                dir="ltr"
            />

            {loading && (
                <Loader2
                    className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin ${isRTL ? 'left-3' : 'right-3'}`}
                    aria-hidden="true"
                />
            )}

            {showList && (
                <ul
                    id={listId}
                    role="listbox"
                    className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto"
                >
                    {status === 'ok' && suggestions.map((s, index) => (
                        <li
                            key={s.id}
                            id={`${listId}-${index}`}
                            role="option"
                            aria-selected={index === activeIndex}
                            // mouseDown (et non click) : se déclenche avant le blur de l'input
                            onMouseDown={e => { e.preventDefault(); select(s); }}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`flex items-start gap-2 px-3 py-2 text-sm cursor-pointer text-left ${index === activeIndex ? 'bg-blue-50' : ''}`}
                        >
                            <MapPin className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                            <span className="flex-1 text-gray-800">
                                {s.label}
                                {s.precision === 'street' && (
                                    <span className="ml-2 text-xs text-amber-600">
                                        {t('register.addressInfo.approximatePosition')}
                                    </span>
                                )}
                            </span>
                        </li>
                    ))}
                    {status === 'empty' && (
                        <li className="px-3 py-2 text-sm text-gray-500 text-left">
                            {t('register.addressInfo.noResults')}
                        </li>
                    )}
                    {status === 'error' && (
                        <li className="px-3 py-2 text-sm text-red-600 text-left">
                            {t('register.addressInfo.searchError')}
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};
